/*
    The purpose of this code is to facilitate the creation of a web application
    that allows users to customize and view avatars with various features such as heads, eyes, mouths, clothes, and accessories.
    It provides a way to generate random avatar data, translate user data into a format suitable for rendering, and manage the customization options available for avatars.
*/

import { AvatarCanvas } from "./AvatarCanvas";
import { Avatar } from "./Avatar";
import { ItemDatabase } from "./ItemDatabase";
import { AvatarController } from "./AvatarController";
import type { UserData } from "./Customization";

// const FETCH_AVATAR_URL = "/api/avatars/get?userId=";
// const DATABASE_URL = "/data/customizationV2.json";

export class PixlApp {
    private _database: ItemDatabase;
    private _parent: HTMLElement | null = null;

    private _canvas: AvatarCanvas;

    private _lastDelta: number = 0;

    private _controllers: AvatarController[] = [];

    public ground: number = 10;

    private _testMode: boolean = false;

    get canvas() {
        return this._canvas;
    }

    constructor(database: ItemDatabase) {
        this._database = database;

        const canvas = document.createElement("canvas");
        this._canvas = new AvatarCanvas(canvas);
    }

    // test() {
    //     const userId = "524796577";
    //     const name = "Mythic";
    //     this.addMember(userId, name);
    // }

    attachTo = (node: HTMLElement): void => {
        // Detatch the canvas if already attached.
        if (this._parent?.contains(this._canvas.canvas))
            this._parent?.removeChild(this._canvas.canvas);

        // Attach the canvas to the new parent.
        this._parent = node;
        this._parent.appendChild(this._canvas.canvas);

        this._canvas.width = node.offsetWidth;
        this._canvas.height = node.offsetHeight;
    };

    detach = (): void => {
        if (this._parent?.contains(this._canvas.canvas))
            this._parent?.removeChild(this._canvas.canvas);
    };

    addController = (controller: AvatarController) => {
        this._controllers.push(controller);
    };

    addMember = (data: UserData) => {
        // Remove avatars that already exist with the given id to reset them.
        this._controllers = this._controllers.filter(
            (c) => c.userId != data.userId
        );

        // Fetch the user data from the server.
        // let f = await fetch(url + userId);
        // let data = await f.json();

        if (!data || !data.avatar) {
            console.error("No avatar data found for user:", data.userId);
            return null;
        }

        data.avatar = this._database.translateAvatarData(data.avatar);

        // Create a new avatar.
        var av = Avatar.fromData(data);

        // av.animator.addAnimations(
        //     JSON.parse(JSON.stringify(DEFAULT_ANIMATIONS))
        // );
        //av.animator.play("idleRight");

        var avc = new AvatarController(data.userId, data.username, av);

        // Add the avatar to the list.
        this._controllers.push(avc);
        return avc;
    };

    removeController = (controller: AvatarController) => {
        this._controllers = this._controllers.filter(
            (c) => c.userId != controller.userId
        );
    };

    enableTest = () => {
        this._testMode = true;
    };

    clear = () => {
        this._controllers = [];
    };

    hasController = (controller: AvatarController) => {
        return this._controllers.includes(controller);
    };

    killAll = () => {
        this._controllers.forEach((c) => c.die());
    };

    jumpLoop = () => {
        this._controllers.forEach((c) => {
            c.jump();
        });
    };

    avatarsExit = () => {
        // this._controllers.forEach((c) => {
        //     c.jumpLoop = false;
        //     c.targetLocation = {
        //         x: Math.random() < 0.5 ? -500 : this.canvas.width + 500,
        //         y: c.location.y,
        //     };
        //     c.idleTime = 0;
        //     c.run();
        //     c.onTargetDestinationReached = () => {
        //         c.dispose();
        //     };
        // });
    };

    render = (timestamp: number = 0) => {
        // Don't bother drawing anything if the canvas is not present.
        if (!this._parent) return;

        // Get the delta time since the last frame
        if (this._lastDelta === 0) this._lastDelta = timestamp;

        const deltaTime = (timestamp - this._lastDelta) / 1000;

        this._canvas.validateSize();

        // Clear the canvas before drawing
        this._canvas.clear();

        if (this._testMode) {
            var dark = false;
            for (let x = 0; x < this._canvas.width; x += 16) {
                for (let y = 0; y < this._canvas.height; y += 16) {
                    this._canvas.fillStyle = dark ? "#0c0c0c" : "#1c1c1c";
                    this._canvas.fillRect({
                        x: x,
                        y: y,
                        width: 16,
                        height: 16,
                    });

                    dark = !dark;
                }
                dark = !dark;
            }
        }
        // Filter disposed avatars out to avoid rendering them
        // and to keep the list clean.
        // This is important to avoid memory leaks and performance issues.
        // It also helps to keep the list of avatars up-to-date.
        this._controllers = this._controllers.filter((c) => !c.disposed);

        // Draw the avatars
        this._controllers
            .sort((a, b) => a.avatar.zIndex - b.avatar.zIndex)
            .forEach((c) => {
                c.update(deltaTime);
                c.avatar.render(this._canvas, deltaTime);
            });

        this._lastDelta = timestamp;
        requestAnimationFrame(this.render);
    };
}
